import { Error, GutterSeparator } from "@qw/ui";
import { Fragment, type ReactNode } from "react";
import { Message } from "@/message/message";
import { useConversation } from "@/providers/use-conversation";
import { latestThreadMessageId } from "./latest-thread-message";
import { THREAD_READING_ORDER } from "./thread-reading-order";
import { useThreadReadingOrder } from "./use-thread-reading-order";

export const ConversationMessages = ({ afterNewestMessage }: { afterNewestMessage?: ReactNode }) => {
  const { inboxConversation, loading, error } = useConversation();
  const { readingOrder } = useThreadReadingOrder();

  if (error) {
    return <Error error={error} className="p-4 sm:p-6 md:p-8" />;
  }

  if (!loading && (!inboxConversation || inboxConversation?.messages.length === 0)) {
    return (
      <Error title="No messages found" message="This conversation appears to be empty." className="p-4 sm:p-6 md:p-8" />
    );
  }

  const messages = inboxConversation?.messages || [];
  // Reverse here and nowhere else: `latestThreadMessageId` reads the last message as the newest one
  // to reference a Reply/Reply all/Forward, so reversing further up would point every
  // conversation-level draft at the oldest message. Copy — Apollo freezes query results.
  const orderedMessages = readingOrder === THREAD_READING_ORDER.newestFirst ? [...messages].reverse() : messages;
  const newestMessageId = latestThreadMessageId(messages);

  return (
    <div>
      {orderedMessages.map((message) => (
        <Fragment key={message.id}>
          <Message message={message} />
          {/* Before the separator, not after: under oldest-first the newest message is the last
              one, so the slot has to sit flush against it exactly as it did when the two were
              siblings of this list. A thread with no newest message to anchor never gets here —
              unloaded messages and empty threads both return above. */}
          {message.id === newestMessageId && afterNewestMessage}
          <GutterSeparator />
        </Fragment>
      ))}
    </div>
  );
};
